import { useContext, useState, useEffect } from 'react';
import './SiteHeader.css';
import PlusButton from '../PlusButton/PlusButton';
import { AuthenticatedSessionContext } from '../../authenticatedSession/AuthenticatedSessionContext';
import { useMediaQuery } from '@mui/material'; // <-- Import to check screen size
import TimeEntryModal from '../TimeEntryModal/TimeEntryModal';
import {
  Timer,
  Pause,
  Square,
  Search,
  TimerReset,
  Sparkles,
} from 'lucide-react';
import { spotlight } from '@mantine/spotlight';
import {
  Burger,
  Drawer,
  ScrollArea,
  Box,
  Anchor,
  Group,
  Stack,
  Switch,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMantineColorScheme } from '@mantine/core';
import { Sun, Moon } from 'lucide-react';
import UserMenu from '../SidebarMenu/UserMenu';
import type { SidebarMenuItem } from '../SidebarMenu/SidebarMenu';

interface StopwatchState {
  isRunning: boolean;
  isStopped: boolean;
  startTime: number | null;
  elapsedTime: number;
}

const Header = ({
  menuItems,
  openMobileMenu,
  onOpenScoutChat,
}: {
  menuItems: SidebarMenuItem[];
  openMobileMenu: () => void;
  onOpenScoutChat?: () => void;
}) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const isMobile = useMediaQuery('(max-width: 768px)'); // <-- Check if it's mobile view
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  const [stopwatch, setStopwatch] = useState<StopwatchState>({
    isRunning: false,
    isStopped: false,
    startTime: null,
    elapsedTime: 0,
  });
  const [showTimeEntry, setShowTimeEntry] = useState(false);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (stopwatch.isRunning) {
      intervalId = setInterval(() => {
        setStopwatch((prev) => ({
          ...prev,
          elapsedTime: Date.now() - (prev.startTime || Date.now()),
        }));
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [stopwatch.isRunning]);

  const handleStartStop = () => {
    if (stopwatch.isRunning) {
      // When pausing, also set isStopped to true
      setStopwatch((prev) => ({
        ...prev,
        isRunning: false,
        isStopped: true,
      }));
    } else {
      // When starting/resuming, clear isStopped
      setStopwatch((prev) => ({
        ...prev,
        isRunning: true,
        isStopped: false,
        startTime: Date.now() - prev.elapsedTime,
      }));
    }
  };

  const handleStopClick = () => {
    setStopwatch((prev) => ({
      ...prev,
      isRunning: false,
      isStopped: true,
    }));
    setShowTimeEntry(true);
  };

  const handleReset = () => {
    setStopwatch({
      isRunning: false,
      isStopped: false,
      startTime: null,
      elapsedTime: 0,
    });
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return `${hours.toString().padStart(2, '0')}:${(minutes % 60)
      .toString()
      .padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const convertToDecimalHours = (ms: number) => {
    const hours = ms / (1000 * 60 * 60);
    return Math.ceil(hours * 10) / 10; // Round up to nearest 0.1
  };

  return authSession ? (
    <>
      <div className='site-header'>
        {isMobile && (
          <Burger
            opened={drawerOpened}
            onClick={toggleDrawer}
            hiddenFrom='sm'
            style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              zIndex: 1000,
            }}
          />
        )}
        <div className={`stopwatch-widget ${isMobile ? 'mobile' : ''}`}>
          <button
            className={
              stopwatch.isRunning
                ? 'isrunning-stopwatch-button'
                : 'stopwatch-button'
            }
            onClick={handleStartStop}
          >
            {stopwatch.isRunning ? <Pause size={16} /> : <Timer size={16} />}
          </button>
          <div className='stopwatch-display'>
            {formatTime(stopwatch.elapsedTime)}
            {stopwatch.isRunning && (
              <div className='site-header-hint'>Keep tab open</div>
            )}
          </div>
          {stopwatch.isRunning && (
            <button
              className='outline-stopwatch-button'
              onClick={handleStopClick}
            >
              <Square size={16} />
            </button>
          )}
          {stopwatch.isStopped && !stopwatch.isRunning && (
            <button className='outline-stopwatch-button' onClick={handleReset}>
              <TimerReset size={16} />
            </button>
          )}
        </div>
        <button
          className={`search-button ${isMobile ? 'mobile' : ''}`}
          onClick={() => spotlight.open()}
        >
          <Search size={16} />
          {!isMobile && (
            <>
              <div className='site-header-hint'>⌘K to search</div>
              <span
                className='scout-chat-entry-icon'
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (onOpenScoutChat) {
                    onOpenScoutChat();
                    return;
                  }
                  navigate('/chat');
                }}
                role='button'
                aria-label='Open Scout chat'
                title='Open Scout chat'
              >
                <Sparkles size={16} strokeWidth={1.75} />
              </span>
            </>
          )}
        </button>
        <PlusButton />
      </div>

      {/* Mobile drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size='100%'
        padding='md'
        title='Navigation'
        hiddenFrom='sm'
        zIndex={1000000}
      >
        <ScrollArea h='calc(100vh - 80px)' mx='-md'>
          <Box px='md'>
            {menuItems
              .filter((item) => !item.isHidden)
              .map((item, index) => (
                <Anchor
                  key={index.toString()}
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                    closeDrawer();
                  }}
                  c='var(--mantine-color-text)'
                  style={{
                    textDecoration: 'none',
                    display: 'block',
                    padding: '0.75rem 0',
                  }}
                >
                  {item.label}
                  {item.badgeCount && item.badgeCount > 0 ? (
                    <span
                      style={{
                        marginLeft: '0.5rem',
                        color: item.badgeColor ?? 'var(--mantine-color-blue-6)',
                      }}
                    >
                      ({item.badgeCount})
                    </span>
                  ) : null}
                </Anchor>
              ))}
          </Box>
          <Box px='md' mt='xl'>
            <Group justify='space-between' align='center' mb='md'>
              <span>Dark Mode</span>
              <Switch
                size='md'
                color='dark.4'
                onLabel={<Sun size={16} />}
                offLabel={<Moon size={16} />}
                checked={colorScheme === 'dark'}
                onChange={() => toggleColorScheme()}
              />
            </Group>
            {authSession && <UserMenu authSession={authSession} />}
          </Box>
        </ScrollArea>
      </Drawer>

      {showTimeEntry && (
        <TimeEntryModal
          fixedMatterId={undefined}
          initialHours={convertToDecimalHours(stopwatch.elapsedTime)}
          onClose={(submitted?: boolean) => {
            setShowTimeEntry(false);
            if (submitted) {
              setStopwatch({
                isRunning: false,
                isStopped: false,
                startTime: null,
                elapsedTime: 0,
              });
            } else {
              console.log('Cancel clicked, setting isStopped to true');
              setStopwatch((prev) => ({
                ...prev,
                isRunning: false,
                isStopped: true,
              }));
            }
          }}
        />
      )}
    </>
  ) : null;
};

export default Header;
